/**
 * Аналитик на каноне: тот же экран, другой счётчик.
 *
 * Разметка, порядок блоков и органы управления остались от прототипа — слева
 * готовые вопросы, справа поле свободного вопроса и ответ. Изменился один
 * слой: числа больше не считаются в браузере из `analysis.json`, их считает
 * шлюз (`/ai/answer`), а печатает общий рендерер конверта
 * (`features/ai/AnswerSpecView`) — тот же, что на канонической станции.
 *
 * ПОЧЕМУ РЕНДЕРЕР ОБЩИЙ, А НЕ СВОЙ. Ответ шлюза — не текст, а спецификация с
 * покрытием, ограничениями, следом валидатора и плашкой об участии модели.
 * Второй рендерер рядом означал бы, что одно и то же утверждение можно
 * показать двумя способами, и однажды они разошлись бы именно в оговорках —
 * то есть в самом проверяемом месте ответа. Стили Astra ложатся сверху
 * контейнером, внутрь конверта не залезая.
 *
 * СРЕЗ. Дата состояния и «включать ли демо» берутся из общего контекста
 * анализа, а не из локального переключателя экрана: у канонических экранов
 * срез один на приложение, и второй рядом означал бы два разных мира в одном
 * кадре.
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Search, Sparkles } from 'lucide-react';
import {
  ApiError,
  api,
  failureKind,
  request,
  type AiAnswerList,
  type AiEnvelope,
  type AiPlan,
} from '../../api/client';
import { useAnalysisContext } from '../../analysis/context';
import { roleKind, useAuth } from '../../auth/context';
import { AbstentionView, AnswerSpecView } from '../../features/ai/AnswerSpecView';
import { usePageTrail } from '../shell/ShellContext';
import { RECIPE_TOOLS, nearestRecipes } from '../data-source/api/analyst';
import { RECIPES, type RecipeId } from './model';
import { Panel, Title } from './shared';
import { AskAiButton } from './AskAiButton';
import {
  CYCLE_SHARE_METRICS,
  cycleShareFrom,
  withoutShareBlocks,
  withoutVerifiedAxis,
  type CycleShareCounts,
  type MetricRow,
} from './analystAnswer';
import { useRequest } from '../../api/useRequest';
import { DoneSplit } from '../ui/DoneSplit';
import { Skeleton, SkeletonCard, SkeletonList, SkeletonRegion, SkeletonText } from '../ui';
import { isLoading } from '../ui/AsyncBlock';
import { cycleLabel, defaultCycle } from '../data-source/api/compare';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  trailLevel: { id: 'station.analystCanon.trail.level', defaultMessage: 'Район', description: 'Хлебная крошка: уровень просмотра «Район»' },
  title: { id: 'station.analystCanon.title', defaultMessage: 'Аналитик', description: 'Заголовок и хлебная крошка страницы «Аналитик» (хоким, менеджер)' },
  eyebrow: { id: 'station.analystCanon.eyebrow', defaultMessage: 'ДАННЫЕ → ОБЪЯСНЕНИЕ → РЕШЕНИЕ', description: 'Надзаголовок страницы «Аналитик» заглавными буквами; стрелки — часть текста' },
  lead: { id: 'station.analystCanon.lead', defaultMessage: 'Откройте управленческий вопрос. Ответ считает сервер тем же обходом, что и сводку сезона; здесь он только назван и показан.', description: 'Подзаголовок страницы «Аналитик»' },
  questions: { id: 'station.analystCanon.questions', defaultMessage: 'ГОТОВЫЕ ВОПРОСЫ', description: 'Надзаголовок списка готовых вопросов слева, заглавными буквами' },
  serverCounts: { id: 'station.analystCanon.serverCounts', defaultMessage: 'Считает сервер, а не экран', description: 'Жирный заголовок плашки под списком вопросов' },
  serverCountsText: { id: 'station.analystCanon.serverCountsText', defaultMessage: 'Ответ — вызов инструмента реестра; участие ИИ названо в самом ответе.', description: 'Текст плашки под списком вопросов' },
  searchLabel: { id: 'station.analystCanon.search.label', defaultMessage: 'Поиск аналитического вопроса', description: 'aria-label поля свободного вопроса' },
  searchPlaceholder: { id: 'station.analystCanon.search.placeholder', defaultMessage: 'Спросить своими словами: вода, сроки, прошлый сезон…', description: 'Подсказка внутри поля свободного вопроса; перечислены примеры тем' },
  searchButton: { id: 'station.analystCanon.search.button', defaultMessage: 'Разобрать', description: 'Кнопка отправки свободного вопроса на разбор плана; коротко' },
  needSignIn: { id: 'station.analystCanon.needSignIn', defaultMessage: 'Нужен вход', description: 'Заголовок панели ошибки, когда пользователь не вошёл в систему' },
  notParsed: { id: 'station.analystCanon.notParsed', defaultMessage: 'Вопрос не разобран', description: 'Заголовок панели ошибки при сбое разбора свободного вопроса' },
  noAnswer: { id: 'station.analystCanon.noAnswer', defaultMessage: 'Ответа на этот вопрос не будет', description: 'Заголовок панели: система воздержалась от ответа на свободный вопрос; ниже причина' },
  planTitle: { id: 'station.analystCanon.plan.title', defaultMessage: 'Чем предлагается ответить', description: 'Заголовок панели с планом ответа (список инструментов)' },
  planSubtitle: { id: 'station.analystCanon.plan.subtitle', defaultMessage: 'План — это выбор инструментов, а не результат: ни одного числа здесь ещё нет.', description: 'Подзаголовок панели плана ответа' },
  planRun: { id: 'station.analystCanon.plan.run', defaultMessage: 'Ответить по плану', description: 'Кнопка: выполнить предложенный план и получить ответ' },
  freeAnswer: { id: 'station.analystCanon.freeAnswer', defaultMessage: 'Ответ на свободный вопрос', description: 'Заголовок панели с ответом на свободный вопрос' },
  cycleShareLabel: { id: 'station.analystCanon.cycleShare.label', defaultMessage: 'долю по текущему агроциклу', description: 'Что загружается (скрытая подпись скелетона, «Загружаю …»): доля по текущему агроциклу' },
  campaignsSubtitle: { id: 'station.analystCanon.campaigns.subtitle', defaultMessage: 'Открытые кампании на дату, не агроцикл: какие из них ещё ждут отчётов.', description: 'Подзаголовок панели ответа «Как идут сезонные работы?» при показанной панели текущего агроцикла' },
  campaignsInfo: { id: 'station.analystCanon.campaigns.info', defaultMessage: 'Доля выполненного по агроциклу — в панели «Текущий агроцикл» выше. {note}', description: 'Подсказка «i» у панели ответа; {note} — готовая заметка о методе от источника' },
  answerLabel: { id: 'station.analystCanon.answer.label', defaultMessage: 'ответ сервера', description: 'Что загружается (скрытая подпись скелетона, «Загружаю …»): ответ сервера' },
  answerNone: { id: 'station.analystCanon.answer.none', defaultMessage: 'Ответ ещё не запрашивался.', description: 'Пустое состояние панели ответа' },
  answerSignIn: { id: 'station.analystCanon.answer.signIn', defaultMessage: 'Нужен вход: этот расчёт виден только участнику.', description: 'Сообщение вместо ответа, когда пользователь не вошёл' },
  cycleTitle: { id: 'station.analystCanon.cycle.title', defaultMessage: 'Текущий агроцикл: {label}', description: 'Заголовок панели доли выполнения по текущему агроциклу; {label} — название агроцикла («Хлопок 2026»)' },
  cycleSubtitle: { id: 'station.analystCanon.cycle.subtitle', defaultMessage: 'Тот же срез и то же определение «Выполнено», что в «Обзоре района».', description: 'Подзаголовок панели доли по текущему агроциклу; «Выполнено» и «Обзор района» — названия показателя и экрана' },
  cycleDenominator: { id: 'station.analystCanon.cycle.denominator', defaultMessage: '{count, plural, one {работа на поле агроцикла} few {работы на полях агроцикла} many {работ на полях агроцикла} other {работы на полях агроцикла}}', description: 'Слово без числа: знаменатель полосы выполнения по агроциклу — работы на полях' },
  cycleWindowNote: { id: 'station.analystCanon.cycle.windowNote', defaultMessage: 'Идущие окна — ещё не срок.', description: 'Пояснение под полосой выполнения' },
  journalSummary: { id: 'station.analystCanon.journal.summary', defaultMessage: 'Журнал заданных вопросов', description: 'Заголовок раскрывающегося блока: список вопросов, заданных аналитику' },
  journalLabel: { id: 'station.analystCanon.journal.label', defaultMessage: 'журнал вопросов', description: 'Что загружается (скрытая подпись скелетона, «Загружаю …»)' },
  journalEmpty: { id: 'station.analystCanon.journal.empty', defaultMessage: 'Вопросов ещё не задавали.', description: 'Пустое состояние журнала вопросов' },
  journalWhen: { id: 'station.analystCanon.journal.col.when', defaultMessage: 'Когда', description: 'Колонка таблицы журнала: дата вопроса' },
  journalTool: { id: 'station.analystCanon.journal.col.tool', defaultMessage: 'Инструмент', description: 'Колонка таблицы журнала: каким инструментом отвечали' },
  journalOutcome: { id: 'station.analystCanon.journal.col.outcome', defaultMessage: 'Исход', description: 'Колонка таблицы журнала: чем закончился вопрос' },
});

const RECIPE_IDS = Object.keys(RECIPES) as RecipeId[];

/** Один вызов инструмента: что спросили и чем ответили. */
interface CallResult {
  toolId: string;
  envelope: AiEnvelope | null;
  error: ApiError | null;
}

export function AnalystCanon() {
  const t = useT();
  usePageTrail([{ level: t(m.trailLevel), name: t(m.title) }]);
  const context = useAnalysisContext();
  const auth = useAuth();

  const [selected, setSelected] = useState<RecipeId>('M-CAM-02');
  const [results, setResults] = useState<CallResult[] | null>(null);
  const [asking, setAsking] = useState(true); // эффект ниже стартует сразу: до ответа — скелетон, а не «не запрашивался»

  const [query, setQuery] = useState('');
  const [plan, setPlan] = useState<AiPlan | null>(null);
  const [planEnvelope, setPlanEnvelope] = useState<AiEnvelope | null>(null);
  const [planError, setPlanError] = useState<ApiError | null>(null);
  const [planning, setPlanning] = useState(false);

  const binding = RECIPE_TOOLS[selected];
  const cycleShareState = useCurrentCycleShare();
  const cycleShare = selected === 'M-CAM-02' ? cycleShareState.share : null;
  const cycleShareLoading = selected === 'M-CAM-02' && cycleShareState.loading;
  const view = useMemo(
    () => ({ linkSearch: context.linkSearch, accountScope: roleKind(auth.principal) }),
    [context.linkSearch, auth.principal],
  );

  /**
   * Готовый вопрос выполняется сразу при выборе — как в прототипе, где ответ
   * появлялся без второго жеста. Смена среза перезапускает вызов: ответ,
   * посчитанный на другую дату, рядом с новой датой был бы неправдой.
   */
  useEffect(() => {
    let alive = true;
    setAsking(true);
    setResults(null);
    void (async () => {
      const collected: CallResult[] = [];
      for (const call of binding.calls) {
        try {
          const envelope = await api.aiAnswer(
            {
              tool_id: call.tool_id,
              params: call.params,
              context: { as_of: context.asOf, include_demo: context.includeDemo },
              object_id: null,
              level: call.level,
            },
            {},
          );
          collected.push({ toolId: call.tool_id, envelope, error: null });
        } catch (cause) {
          collected.push({
            toolId: call.tool_id,
            envelope: null,
            error:
              cause instanceof ApiError
                ? cause
                : new ApiError('unknown_error', String(cause), 0),
          });
        }
      }
      if (!alive) return;
      setResults(collected);
      setAsking(false);
    })();
    return () => {
      alive = false;
    };
  }, [binding, context.asOf, context.includeDemo]);

  const choose = (id: RecipeId) => {
    setSelected(id);
    setPlan(null);
    setPlanEnvelope(null);
    setPlanError(null);
  };

  const askPlan = (question: string) => {
    setPlanning(true);
    setPlanError(null);
    setPlanEnvelope(null);
    api
      .aiPlan({ question }, context.params)
      .then(setPlan)
      .catch((cause: unknown) =>
        setPlanError(
          cause instanceof ApiError ? cause : new ApiError('unknown_error', String(cause), 0),
        ),
      )
      .finally(() => setPlanning(false));
  };

  const runPlan = () => {
    if (!plan?.plan) return;
    setPlanning(true);
    setPlanError(null);
    api
      .aiAnswerPlan({ plan: plan.plan, question: query.trim() }, context.params)
      .then((result) => setPlanEnvelope(result.envelope))
      .catch((cause: unknown) =>
        setPlanError(
          cause instanceof ApiError ? cause : new ApiError('unknown_error', String(cause), 0),
        ),
      )
      .finally(() => setPlanning(false));
  };

  const suggestions = plan?.kind === 'abstention' ? nearestRecipes(query) : [];

  return (
    <div className="astra">
      <Title
        eyebrow={t(m.eyebrow)}
        title={t(m.title)}
        text={t(m.lead)}
      />

      <div className="astra-analyst-grid">
        <aside className="astra-question-list" data-guide="ready-questions">
          <div className="astra-eyebrow">{t(m.questions)}</div>
          {RECIPE_IDS.map((id) => (
            <button
              key={id}
              aria-pressed={selected === id}
              className={selected === id ? 'is-selected' : ''}
              onClick={() => choose(id)}
            >
              <span>{t(RECIPES[id].title)}</span>
              <ChevronRight size={16} />
            </button>
          ))}
          <div className="astra-model-state">
            <Sparkles size={18} />
            <strong>{t(m.serverCounts)}</strong>
            <p>{t(m.serverCountsText)}</p>
          </div>
        </aside>

        <div className="astra-answer-column">
          <form
            className="astra-query"
            data-guide="free-question"
            onSubmit={(e) => {
              e.preventDefault();
              if (query.trim()) askPlan(query.trim());
            }}
          >
            <Search size={19} />
            <input
              aria-label={t(m.searchLabel)}
              value={query}
              maxLength={400}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(m.searchPlaceholder)}
            />
            <button className="astra-button astra-button-primary" type="submit" disabled={planning}>
              {t(m.searchButton)}
            </button>
          </form>

          {planError ? (
            <Panel
              title={
                failureKind(planError) === 'unauthenticated'
                  ? t(m.needSignIn)
                  : t(m.notParsed)
              }
            >
              <p>{planError.message}</p>
            </Panel>
          ) : null}

          {plan?.kind === 'abstention' && plan.abstention ? (
            <Panel title={t(m.noAnswer)}>
              <AbstentionView envelope={plan.abstention} />
              {suggestions.length > 0 ? (
                <div className="astra-chips">
                  {suggestions.map((id) => (
                    <button key={id} onClick={() => choose(id)}>
                      {t(RECIPES[id].title)}
                    </button>
                  ))}
                </div>
              ) : null}
            </Panel>
          ) : null}

          {plan?.kind === 'plan' && plan.plan ? (
            <Panel
              title={t(m.planTitle)}
              subtitle={t(m.planSubtitle)}
            >
              <ol>
                {(plan.plan.calls ?? []).map((call, index) => (
                  <li key={index}>{call.tool_id}</li>
                ))}
              </ol>
              <div className="astra-answer-actions">
                <button
                  className="astra-button astra-button-primary"
                  onClick={runPlan}
                  disabled={planning}
                >
                  {t(m.planRun)}
                </button>
              </div>
            </Panel>
          ) : null}

          {planEnvelope ? (
            <Panel title={t(m.freeAnswer)}>
              <AnswerSpecView envelope={withoutVerifiedAxis(planEnvelope)} view={view} />
            </Panel>
          ) : null}

          {cycleShare ? <div data-guide="cycle-share"><CurrentCycleShare share={cycleShare} /></div> : cycleShareLoading ? (
            <SkeletonCard label={t(m.cycleShareLabel)}>
              {/* Подпись, три полосы долей и знаменатель — в высоту готовой панели (замер 6al.49). */}
              <div aria-hidden="true" style={{ height: 176, display: 'grid', alignContent: 'space-between' }}>
                <Skeleton width="70%" className="skel--caption" />
                {[0, 1, 2].map((i) => <Skeleton key={i} variant="block" width="100%" height={18} />)}
                <Skeleton width="90%" className="skel--caption" />
              </div>
            </SkeletonCard>
          ) : null}

          <div data-guide="answer">
          <Panel
            title={t(RECIPES[selected].title)}
            subtitle={cycleShare ? t(m.campaignsSubtitle) : undefined}
            info={cycleShare ? t(m.campaignsInfo, { note: binding.note }) : binding.note}
          >
            {asking ? (
              <SkeletonRegion label={t(m.answerLabel)} minHeight={520}><SkeletonText lines={8} /></SkeletonRegion>
            ) : results === null || results.length === 0 ? (
              <p className="astra-empty">{t(m.answerNone)}</p>
            ) : (
              results.map((result) => (
                <section key={result.toolId}>
                  {results.length > 1 ? (
                    <div className="astra-eyebrow">{result.toolId}</div>
                  ) : null}
                  {result.error ? (
                    <p className="astra-empty">
                      {failureKind(result.error) === 'unauthenticated'
                        ? t(m.answerSignIn)
                        : result.error.message}
                    </p>
                  ) : result.envelope ? (
                    <AnswerSpecView
                      envelope={
                        cycleShare
                          ? withoutShareBlocks(result.envelope)
                          : withoutVerifiedAxis(result.envelope)
                      }
                      view={view}
                    />
                  ) : null}
                </section>
              ))
            )}
          </Panel>
          </div>

          <div className="astra-answer-actions" data-guide="analyst-ask">
            <AskAiButton />
          </div>

          <AnswerJournal />
        </div>
      </div>
    </div>
  );
}

/**
 * Доля по ТЕКУЩЕМУ циклу (журнал №68): тот же цикл, что у «Обзора района»
 * (`defaultCycle`), и те же определения — слой метрик (agroai-6al.12):
 * «Выполнено» = `reports.done_pct`, как KPI Обзора. Инструмент шлюза цикла
 * не знает и считает по всем открытым кампаниям, поэтому при показанной
 * панели цикла доля рецепта скрыта (`withoutShareBlocks`, 6al.13) — одна
 * цифра на всех экранах.
 */
interface CycleShare extends CycleShareCounts {
  label: string;
}

function useCurrentCycleShare(): { share: CycleShare | null; loading: boolean } {
  const ctx = useAnalysisContext();
  const base = ctx.params;
  const cycles = useRequest(() => api.seasonCycles(base), [ctx.key]);
  const items = cycles.data?.items ?? [];
  const cycleId = defaultCycle(items);
  const cycle = items.find((c) => c.cycle === cycleId) ?? null;
  const metrics = useRequest(
    () =>
      cycleId
        ? request<{ metrics: MetricRow[] }>('/ai/metrics', {
            ...base,
            ids: CYCLE_SHARE_METRICS.join(','),
            cycle: cycleId,
          }).then((r) => ({ ...r, cycle: cycleId }))
        : Promise.resolve(null),
    [ctx.key, cycleId],
  );
  const counts = cycleShareFrom(metrics.data?.metrics);
  // Панель цикла ещё не может сказать «нет»: скелетон на время обоих запросов.
  // Ответ метрик — про КОНКРЕТНЫЙ цикл: в кадре, где цикл уже известен, а
  // запрос по нему ещё не стартовал, прежний пустой ответ — не «нет» (6al.49).
  const metricsForCycle = metrics.data?.cycle === cycleId;
  const loading =
    isLoading(cycles) || (cycleId !== null && cycleId !== undefined && metrics.state !== 'error' && !metricsForCycle);
  if (!cycle || !counts) return { share: null, loading };
  return { share: { label: cycleLabel(cycle), ...counts }, loading: false };
}

function CurrentCycleShare({ share }: { share: CycleShare }) {
  const t = useT();
  return (
    <Panel
      title={t(m.cycleTitle, { label: share.label })}
      subtitle={t(m.cycleSubtitle)}
    >
      <DoneSplit
        size="sm"
        counts={{ total: share.total, reported: share.done + share.inProgress, inProgress: share.inProgress }}
        denominator={m.cycleDenominator}
        windowNote={t(m.cycleWindowNote)}
      />
    </Panel>
  );
}

/**
 * Журнал заданных вопросов — раскрытием на том же экране.
 *
 * Журнал читают «сейчас», поэтому контекст анализа в запрос НЕ едет: срез
 * прошлого не открывает записи, которых сегодня видеть нельзя.
 */
function AnswerJournal() {
  const t = useT();
  const [page, setPage] = useState<AiAnswerList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || page !== null) return;
    let alive = true;
    api
      .aiAnswers({})
      .then((value) => {
        if (alive) setPage(value);
      })
      .catch((cause: unknown) => {
        if (!alive) return;
        setError(
          cause instanceof ApiError ? cause : new ApiError('unknown_error', String(cause), 0),
        );
      });
    return () => {
      alive = false;
    };
  }, [open, page]);

  return (
    <details className="astra-answer-method" data-guide="answer-journal" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>{t(m.journalSummary)}</summary>
      {error ? (
        <p>{error.message}</p>
      ) : !open ? null : page === null ? (
        <SkeletonList label={t(m.journalLabel)} items={3} tone="soft" />
      ) : page.items.length === 0 ? (
        <p>{t(m.journalEmpty)}</p>
      ) : (
        <div className="astra-table-wrap">
          <table className="astra-table">
            <thead>
              <tr>
                <th>{t(m.journalWhen)}</th>
                <th>{t(m.journalTool)}</th>
                <th>{t(m.journalOutcome)}</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((row) => (
                <tr key={row.id}>
                  <td>{row.created_at.slice(0, 10)}</td>
                  <td>{row.tool_id || row.plan_tools.join(' · ')}</td>
                  <td>{row.outcome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}
